/**
 * Environmental ruins: eight neutral, weathered structures built on
 * the building kit (buildingKit.ts) with a derelict palette — old metal, rust,
 * concrete, dark glass — and the shared damage language (scorch, rubble, exposed
 * frame, bent posts), plus the pastoral touch of vegetation reclaiming them.
 *
 * Design reference: design_sheets/ruins.html
 */

import * as THREE from 'three';
import type { RuinType, RuinState, TeamPalette } from '../types';
import { TEAMS, REFERENCE, BUILDING_PERSON_SCALE } from '../constants';
import {
  KIT, KitRng, mat, box, cyl, sphere, torus, at, group,
  pad, doorway, windowPanel, pipeRun, vent, solarPanel, lightPole, signPost, crate, barrel, pallet, ladder, matE,
  scorch, rubble, exposedFrame, bentPost,
  type Detail, CLASSIC_DETAIL, pAt, windMount, pDoor, pWindowRows, pStairs, pLadder, pBarrel, pCrate, pPallet, pSandbags,
} from './buildingKit';

/** Derelict colours, in TeamPalette shape so the kit can dress the ruins. */
export const DERELICT: TeamPalette = {
  name: 'Derelict', tagline: '',
  primary: '#5a5a68',   // weathered metal
  accent: '#3a3a44',    // dark metal
  visor: '#506068',     // dead glass
  detail: '#787880',    // light metal
};
const P = { metal: '#5a5a68', metalDk: '#3a3a44', metalLt: '#787880', rust: '#6a4a38', rustDk: '#4a3226', concrete: '#4a4a50', glass: '#506068', wire: '#484850', earth: '#2a2a30', moss: '#55704a', mossLt: '#6f8a5c' };

export const RUIN_NAMES: Record<RuinType, string> = {
  antenna: 'Abandoned Antenna', refinery: 'Derelict Refinery', surveyStation: 'Old Survey Station', landingPad: 'Wrecked Landing Pad',
  supplyCache: 'Collapsed Supply Cache', weatherStation: 'Damaged Weather Station', habitat: 'Abandoned Habitat', commsRelay: 'Ruined Comms Relay',
};

// ---------------------------------------------------------------------------
// ruin-specific pieces
// ---------------------------------------------------------------------------
/** 67.36: a pennant's mount at the tip of a standing mast `h` long (a sibling of the mast, where its tilt puts the tip). */
function tipMount(mast: THREE.Object3D, h: number): THREE.Object3D {
  mast.updateMatrix();
  const tip = new THREE.Vector3(0, h / 2, 0).applyMatrix4(mast.matrix);
  return windMount('pennant', tip.x, tip.y, tip.z);
}
/** Low green tufts where the planet is taking the place back. */
function reclaim(rng: KitRng, count: number, radius: number): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < count; i++) {
    const a = rng.range(0, Math.PI * 2), d = rng.range(radius * 0.4, radius);
    const s = rng.range(0.025, 0.05);
    const t = at(sphere(s, mat(i % 3 === 0 ? P.mossLt : P.moss), 7, 5), Math.cos(a) * d, s * 0.5, Math.sin(a) * d);
    t.scale.set(1, 0.6, 1);
    g.add(t);
  }
  return g;
}
/** Rust streaks: thin dark boxes on a face. */
function rustPatch(w: number, h: number): THREE.Mesh { return box(w, h, 0.006, mat(P.rust)); }
/** Guy wire from a mast point down to an anchor stake. */
function guyWire(from: THREE.Vector3, to: THREE.Vector3): THREE.Group {
  const dir = to.clone().sub(from), len = dir.length();
  const wire = cyl(0.004, 0.004, len, mat(P.wire), 4);
  wire.position.copy(from).add(to).multiplyScalar(0.5);
  wire.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  const stake = at(cyl(0.012, 0.015, 0.08, mat(P.metalDk), 5), to.x, to.y + 0.03, to.z);
  return group(wire, stake);
}
function tripod(h: number, spread: number): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const leg = cyl(0.008, 0.01, h, mat(P.metalLt), 4);
    leg.position.set(Math.cos(a) * spread / 2, h / 2, Math.sin(a) * spread / 2);
    leg.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(-Math.cos(a) * spread, h, -Math.sin(a) * spread).normalize());
    g.add(leg);
  }
  return g;
}

// 1 — ABANDONED ANTENNA: tall mast on a tripod, dish half-detached, guy wires, junction box
function antenna(rng: KitRng, d: Detail): THREE.Group {
  const PS = d.u > 0;
  const g = group(pad(DERELICT, 0.5, 0.5, 0.06));
  g.add(at(tripod(0.32, 0.26), 0, 0.06, 0));
  const mast = at(cyl(0.025, 0.035, 1.15, mat(P.metal), 6), 0, 0.62, 0, 0, 0, 0.06);
  g.add(mast, tipMount(mast, 1.15));
  if (PS) { mast.add(pAt(d, 0, 0.072 - 0.62, 0.035, 0, pLadder(1.15 * d.u - 2.0))); g.add(pAt(d, 0, 0, 0.25, 0, pStairs(0.072 * d.u))); }   // 63.7: a climbing ladder up the mast, steps onto the pad
  for (let i = 0; i < 3; i++) g.add(at(box(0.22, 0.014, 0.014, mat(P.metalDk)), 0.02 * i, 0.36 + i * 0.28, 0, 0, i * 0.5, 0));
  g.add(at(rustPatch(0.05, 0.1), 0.028, 0.5, 0.0, 0, 0, 0.06), at(rustPatch(0.04, 0.07), -0.03, 0.85, 0.01, 0, 1.2, 0.06));
  const dish = at(new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.4), (() => { const m = mat(P.metalLt); m.side = THREE.DoubleSide; return m; })()), 0.1, 1.1, 0.06, 0.9, 0, 0.5);
  g.add(dish, at(cyl(0.008, 0.008, 0.2, mat(P.metal), 4), 0.05, 1.05, 0.03, 0, 0, 0.6));
  g.add(guyWire(new THREE.Vector3(0.03, 0.9, 0), new THREE.Vector3(0.38, 0.0, 0.3)), guyWire(new THREE.Vector3(-0.02, 0.9, 0), new THREE.Vector3(-0.42, 0.0, 0.1)));
  g.add(at(bentPost(0.3, P.wire), -0.25, 0.0, -0.3));                                         // third wire snapped, lying down
  g.add(at(box(0.1, 0.12, 0.08, mat(P.metalDk)), 0.16, 0.12, 0.0), at(box(0.07, 0.04, 0.006, mat(KIT.glassDark)), 0.16, 0.14, 0.041));   // junction box + dead panel
  g.add(at(pipeRun(0.16, 0.008), 0.1, 0.08, 0.14, 0, 0.3, 0));
  g.add(rubble(DERELICT, rng, 4, 0.3), reclaim(rng, 6, 0.4));
  return g;
}

// 2 — DERELICT REFINERY: processor boxes, horizontal tank, pipe network, stack
function refinery(rng: KitRng, d: Detail): THREE.Group {
  const PS = d.u > 0;
  const g = group(pad(DERELICT, 0.9, 0.7, 0.05, true));
  g.add(at(box(0.26, 0.36, 0.34, mat(P.metal)), -0.08, 0.23, 0));
  g.add(at(box(0.2, 0.3, 0.34, mat(P.metal)), 0.2, 0.19, 0.04, 0, 0, 0.15));                  // tilted unit
  if (PS) g.add(pAt(d, 0, 0.05, 0.171, 0, pDoor(DERELICT, true), pWindowRows(DERELICT, true, 0.26 * d.u, 2, { from: 1 })), pAt(d, 0, 0, 0.35, 0, pStairs(0.062 * d.u)), pAt(d, -0.211, 0.05, 0, -Math.PI / 2, pLadder(0.36 * d.u)));   // 63.7
  else g.add(at(windowPanel(DERELICT, true, 0.1, 0.08), -0.08, 0.3, 0.172));
  g.add(at(rustPatch(0.12, 0.1), -0.08, 0.15, 0.172), at(rustPatch(0.06, 0.14), 0.26, 0.2, 0.06, 0, 0, 0.15));
  const tank = group(at(cyl(0.14, 0.14, 0.36, mat(P.metalDk), 12), 0, 0, 0, 0, 0, Math.PI / 2), at(torus(0.14, 0.012, mat(P.rust), 6, 16), -0.1, 0, 0, 0, Math.PI / 2, 0), at(torus(0.14, 0.012, mat(P.rust), 6, 16), 0.1, 0, 0, 0, Math.PI / 2, 0));
  g.add(at(tank, -0.3, 0.19, 0.2));
  for (const sx of [-1, 1]) g.add(at(box(0.03, 0.1, 0.03, mat(P.metalDk)), -0.3 + sx * 0.1, 0.1, 0.2));   // tank cradles
  g.add(at(pipeRun(0.4, 0.022), 0.05, 0.44, 0.12), at(pipeRun(0.22, 0.022), 0.05, 0.44, -0.12, 0, 0, 0.3), at(pipeRun(0.2, 0.018), -0.1, 0.3, 0.2, 0, 0, Math.PI / 2));
  g.add(at(torus(0.035, 0.008, mat(P.rust), 6, 12), 0.05, 0.5, 0.12, Math.PI / 2));             // valve wheel
  g.add(at(cyl(0.022, 0.022, 0.16, mat(P.rust), 6), 0.32, 0.03, -0.18, 0, 0, Math.PI / 2));    // fallen pipe
  g.add(at(vent(0.26), -0.15, 0.41, -0.08));
  if (PS) g.add(pAt(d, 0.4, 0.062, 0.22, 0, pBarrel(P.rustDk)), pAt(d, 0.41, 0.062, -0.1, 0, at(pBarrel(P.metalDk), 0.44, 0.29, 0, 0, 0, Math.PI / 2 - 0.1)));
  else g.add(at(barrel(P.rustDk, 0.05, 0.13), 0.4, 0.05, 0.22), at(barrel(P.metalDk, 0.05, 0.13), 0.41, 0.05, -0.1, 0, 0, Math.PI / 2 - 0.1));
  g.add(scorch(0.3), rubble(DERELICT, rng, 5, 0.42), reclaim(rng, 4, 0.45));
  return g;
}

// 3 — OLD SURVEY STATION: low building missing a wall, roof askew, tripod instrument
function surveyStation(rng: KitRng, d: Detail): THREE.Group {
  const PS = d.u > 0;
  const g = group(pad(DERELICT, 0.6, 0.5, 0.05));
  g.add(at(box(0.5, 0.25, 0.03, mat(P.metal)), 0, 0.18, -0.19), at(box(0.03, 0.25, 0.4, mat(P.metal)), -0.24, 0.18, 0));
  g.add(at(box(0.03, 0.25, 0.4, mat(P.metal)), 0.3, 0.08, 0.05, 0, 0, 1.1));                    // right wall down
  g.add(at(exposedFrame(0.5, 0.25, 0.4), 0, 0.05, 0));
  g.add(at(box(0.52, 0.02, 0.42, mat(P.metalDk)), -0.02, 0.3, -0.02, 0.06, 0, 0.12));           // roof askew
  if (PS) g.add(pAt(d, 0, 0.05, -0.206, Math.PI, pWindowRows(DERELICT, true, 0.5 * d.u, 1)), pAt(d, -0.256, 0.05, 0.08, -Math.PI / 2, pDoor(DERELICT, true)), pAt(d, -0.3, 0, 0.08, -Math.PI / 2, pStairs(0.062 * d.u)));   // 63.7
  else {
    g.add(at(windowPanel(DERELICT, true, 0.14, 0.08), -0.1, 0.22, -0.172));
    g.add(at(doorway(DERELICT, true, 0.12, 0.2), -0.238, 0.05, 0.08, 0, -Math.PI / 2, 0));
  }
  const inst = group(tripod(0.34, 0.2), at(box(0.06, 0.04, 0.06, mat(P.glass)), 0, 0.36, 0), at(cyl(0.01, 0.01, 0.08, mat(P.metalLt), 6), 0.03, 0.4, 0, Math.PI / 2 - 0.4));
  if (PS) { inst.scale.setScalar(1.6 / 0.4); inst.rotation.z = 0.1; g.add(pAt(d, 0.3, 0.0, -0.26, 0.3, inst)); }   // 63.7: a surveyor's instrument, a person tall
  else g.add(at(inst, 0.3, 0.0, -0.26, 0, 0.3, 0.1));
  g.add(at(cyl(0.005, 0.005, 0.4, mat(P.wire), 4), 0.15, 0.01, -0.22, 0, 0, Math.PI / 2));
  if (PS) g.add(pAt(d, -0.1, 0.062, 0.08, 0.3, pCrate(DERELICT)), pAt(d, 0.12, 0.062, 0.1, 0, pPallet(), at(pCrate(DERELICT, REFERENCE.crate.small), 0, 0.14, 0)));
  else g.add(at(crate(DERELICT, 0.1), -0.1, 0.05, 0.08), at(pallet(0.18, 0.14), 0.12, 0.05, 0.1));
  g.add(at(rustPatch(0.1, 0.08), 0, 0.15, -0.204), rubble(DERELICT, rng, 4, 0.35), reclaim(rng, 5, 0.38));
  return g;
}

// 4 — WRECKED LANDING PAD: ring-marked platform, scorch, bent rail posts, dead beacons
function landingPad(rng: KitRng, d: Detail): THREE.Group {
  const PS = d.u > 0;
  const g = group(at(cyl(0.5, 0.55, 0.08, mat(P.concrete), 16), 0, 0.04, 0));
  g.add(at(torus(0.42, 0.01, mat(P.metalLt), 4, 32), 0, 0.085, 0, Math.PI / 2), at(torus(0.2, 0.01, mat(P.metalLt), 4, 24), 0, 0.085, 0, Math.PI / 2));   // markings
  for (let i = 0; i < 4; i++) { const a = i * Math.PI / 2 + Math.PI / 4; g.add(at(box(0.16, 0.012, 0.03, mat(P.metalLt)), Math.cos(a) * 0.31, 0.085, Math.sin(a) * 0.31, 0, -a, 0)); }
  const burn = scorch(0.26); burn.position.y = 0.088; g.add(burn);
  g.add(at(box(0.4, 0.006, 0.015, mat(P.earth)), 0.1, 0.086, 0.1, 0, 0.7, 0), at(box(0.25, 0.006, 0.012, mat(P.earth)), -0.15, 0.086, -0.12, 0, -0.4, 0));   // cracks
  const post = (x: number, z: number, rz = 0, rx = 0, dead = true) => group(
    at(cyl(0.02, 0.022, 0.4, mat(P.metal), 6), x, 0.28, z, rx, 0, rz),
    at(box(0.06, 0.06, 0.06, mat(dead ? KIT.glassDark : P.glass)), x, 0.5, z, rx, 0, rz),
  );
  g.add(post(0.36, 0), post(0, 0.36), post(-0.36, 0, 0.35), post(0, -0.38, 0, 0.7));
  g.add(at(cyl(0.02, 0.02, 0.2, mat(P.rust), 6), -0.15, 0.1, -0.3, 0, 0, Math.PI / 2));          // snapped rail
  if (PS) {   // 63.7: steps up onto the pad at two sides; the cargo at its own size
    g.add(pAt(d, 0.28, 0.088, 0.28, 0.4, pPallet(), at(pCrate(DERELICT), 0, 0.14, 0)), pAt(d, -0.3, 0.088, 0.26, 0, pBarrel(P.rustDk)), pAt(d, 0.3, 0.088, -0.2, 0.5, pCrate(DERELICT, REFERENCE.crate.large)));
    g.add(pAt(d, 0, 0, 0.52, 0, pStairs(0.085 * d.u, 1.6)), pAt(d, 0, 0, -0.52, Math.PI, pStairs(0.085 * d.u, 1.6)));
  } else g.add(at(pallet(), 0.28, 0.08, 0.28), at(barrel(P.rustDk, 0.05, 0.13), -0.3, 0.08, 0.26), at(crate(DERELICT, 0.1), 0.3, 0.08, -0.2));
  g.add(rubble(DERELICT, rng, 4, 0.45), reclaim(rng, 3, 0.55));
  return g;
}

// 5 — COLLAPSED SUPPLY CACHE: half-buried bunker, berm, jammed door, spilled crates
function supplyCache(rng: KitRng, d: Detail): THREE.Group {
  const PS = d.u > 0;
  const g = new THREE.Group();
  const berm = at(new THREE.Mesh(new THREE.SphereGeometry(0.42, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), mat(P.earth)), 0, 0, -0.08); berm.scale.set(1.2, 0.55, 1); g.add(berm);   // earth mound (hemisphere: nothing below ground)
  g.add(at(box(0.5, 0.3, 0.4, mat(P.metalDk)), 0, 0.14, 0, 0.12));
  g.add(at(box(0.54, 0.04, 0.44, mat(P.concrete)), 0, 0.3, -0.02, 0.12));                        // lid slab
  if (PS) g.add(pAt(d, 0, 0, 0.2, 0, pDoor(DERELICT, true)));   // 63.7: the blown door at a person's size
  else g.add(at(doorway(DERELICT, true, 0.22, 0.24), 0, 0.0, 0.2));                                 // blown-open doorway (no lamp: ruins are dead)
  const stack = PS ? pAt(d, 0, 0, 0, 0, pCrate(DERELICT, REFERENCE.crate.large), at(pCrate(DERELICT, REFERENCE.crate.small), 0, REFERENCE.crate.large, 0.05, 0, 0.3, 0), at(pCrate(DERELICT), 0.9, 0, 0.1, 0, 0.5, 0))
    : group(at(crate(DERELICT, 0.1), 0, 0, 0), at(crate(DERELICT, 0.08), 0.0, 0.1, 0.01, 0, 0.3, 0), at(crate(DERELICT, 0.1), 0.14, 0, 0.02, 0, 0.5, 0));
  g.add(at(stack, -0.12, 0.0, 0.3));
  if (PS) {   // 63.7: the spilled cargo and two sandbag walls flanking the door, at their own size
    g.add(pAt(d, 0.3, 0.0, 0.34, 0.8, at(pCrate(DERELICT, REFERENCE.crate.large), 0, 0, 0, 0.2, 0, 0.3)), pAt(d, 0.34, 0.0, 0.14, 0, pBarrel(P.rustDk)));
    for (const sx of [-1, 1]) g.add(pAt(d, sx * 0.17, 0, 0.36, sx * 0.3, pSandbags(3, 2)));
  } else {
    g.add(at(crate(DERELICT, 0.1), 0.3, 0.0, 0.34, 0.2, 0.8, 0.3), at(barrel(P.rustDk, 0.05, 0.12), 0.34, 0.0, 0.14));
    for (let i = 0; i < 6; i++) { const a = -0.6 + i * 0.3; const sb = at(sphere(0.045, mat('#8a7a5a'), 8, 6), Math.sin(a) * 0.36, 0.03, 0.24 + Math.cos(a) * 0.12); sb.scale.set(1.3, 0.6, 1); g.add(sb); }   // sandbags
  }
  g.add(at(lightPole(DERELICT, true, 0.3), -0.36, 0.0, 0.26), rubble(DERELICT, rng, 3, 0.35), reclaim(rng, 7, 0.45));
  return g;
}

// 6 — DAMAGED WEATHER STATION: leaning mast, anemometer, vane, dead instrument screen
function weatherStation(rng: KitRng, d: Detail): THREE.Group {
  const PS = d.u > 0;
  const g = group(pad(DERELICT, 0.36, 0.36, 0.05));
  const mast = at(cyl(0.022, 0.032, 0.9, mat(P.metal), 5), 0, 0.5, 0, 0, 0, 0.1); g.add(mast, tipMount(mast, 0.9));
  if (PS) { mast.add(pAt(d, 0, 0.062 - 0.5, 0.032, 0, pLadder(0.9 * d.u - 2.0))); g.add(pAt(d, 0, 0, 0.18, 0, pStairs(0.062 * d.u))); }   // 63.7: a ladder up the mast, steps onto the pad
  for (let i = 0; i < 4; i++) g.add(at(box(0.12, 0.01, 0.01, mat(P.metalDk)), 0.02 * i, 0.22 + i * 0.2, 0, 0, i * 0.4, 0));
  const hub = group(at(cyl(0.015, 0.015, 0.03, mat(P.metalLt), 6), 0, 0, 0));
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    hub.add(at(cyl(0.005, 0.005, 0.1, mat(P.metal), 4), Math.cos(a) * 0.05, 0, Math.sin(a) * 0.05, 0, -a, Math.PI / 2));
    const cup = at(sphere(0.025, mat(P.metalLt), 6, 4), Math.cos(a) * 0.1, 0, Math.sin(a) * 0.1); cup.scale.set(1, 0.5, 1); hub.add(cup);
  }
  g.add(at(hub, 0.09, 0.96, 0));
  g.add(at(box(0.15, 0.04, 0.01, mat(P.rust)), 0.1, 0.86, 0.03, 0, 0, 0.4));                   // vane
  g.add(at(box(0.08, 0.06, 0.06, mat(P.metalDk)), 0.08, 0.6, 0), at(box(0.05, 0.035, 0.005, mat(KIT.glassDark)), 0.08, 0.6, 0.033));
  g.add(guyWire(new THREE.Vector3(0.05, 0.75, 0), new THREE.Vector3(0.3, 0.0, 0.26)), guyWire(new THREE.Vector3(0.04, 0.75, 0), new THREE.Vector3(-0.28, 0.0, 0.22)));
  g.add(at(solarPanel(DERELICT, true), -0.2, 0.05, -0.18), at(cyl(0.02, 0.02, 0.08, mat(P.metalLt), 6), 0.2, 0.02, 0.15, 0, 0, Math.PI / 2));
  g.add(rubble(DERELICT, rng, 3, 0.3), reclaim(rng, 5, 0.36));
  return g;
}

// 7 — ABANDONED HABITAT: pressurised pod cracked open, struts, porthole, ladder
function habitat(rng: KitRng, d: Detail): THREE.Group {
  const PS = d.u > 0;
  const g = new THREE.Group();
  const pod = group(
    at(cyl(0.25, 0.25, 0.55, mat(P.metal), 12), 0, 0, 0, 0, 0, Math.PI / 2),
    at(new THREE.Mesh(new THREE.SphereGeometry(0.25, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), mat(P.metalDk)), -0.27, 0, 0, 0, 0, Math.PI / 2),
    at(torus(0.2, 0.05, mat(P.metalDk), 8, 14), 0.28, 0, 0, 0, Math.PI / 2, 0),
    at(new THREE.Mesh(new THREE.CircleGeometry(0.17, 14), mat(KIT.glassDark)), 0.29, 0, 0, 0, Math.PI / 2, 0),   // hatch gone: dark interior
    at(box(0.35, 0.01, 0.02, mat(P.earth)), 0, 0.245, 0, 0, 0.2, 0),                              // crack along the top
    at(torus(0.065, 0.012, mat(P.metalLt), 6, 14), 0, 0, 0.255), at(new THREE.Mesh(new THREE.CircleGeometry(0.055, 12), mat(KIT.glassDark)), 0, 0, 0.256),   // porthole
    at(rustPatch(0.14, 0.1), -0.1, 0.02, 0.252), at(rustPatch(0.08, 0.06), 0.12, -0.1, 0.251),
  );
  g.add(at(pod, 0, 0.3, 0));
  for (const [x, z] of [[-0.15, 0.18], [0.15, -0.18], [0.14, 0.17], [-0.14, -0.17]]) g.add(at(box(0.04, 0.16, 0.04, mat(P.metalDk)), x, 0.08, z, z > 0 ? -0.12 : 0.12, 0, x > 0 ? -0.1 : 0.1));
  g.add(at(box(0.2, 0.01, 0.12, mat(P.metalLt)), 0.36, 0.12, 0.05, 0, 0.4, 0.1));                 // bunk dragged outside
  g.add(PS ? pAt(d, 0.1, 0.0, 0.36, 0, at(pLadder(2.9), 0, 0, 0, -0.35, 0, 0)) : at(ladder(0.26), 0.1, 0.0, 0.3, -0.35, 0, 0), at(vent(0.08), -0.1, 0.52, -0.05, 0, 0, 0.3));
  if (PS) g.add(pAt(d, 0.345, 0, 0, Math.PI / 2, pStairs((0.3 - 0.17) * d.u, 1.2)));   // 63.7: steps up to the open hatch
  g.add(at(exposedFrame(0.2, 0.14, 0.14), 0.45, 0, -0.18));
  const burn = scorch(0.22); burn.position.set(0.4, 0.004, 0.2);
  g.add(burn, rubble(DERELICT, rng, 5, 0.45), reclaim(rng, 5, 0.42));
  return g;
}

// 8 — RUINED COMMS RELAY: lower mast standing, upper snapped and fallen, dishes on the ground
function commsRelay(rng: KitRng, d: Detail): THREE.Group {
  const PS = d.u > 0;
  const g = group(pad(DERELICT, 0.4, 0.4, 0.06));
  g.add(at(tripod(0.3, 0.24), 0, 0.06, 0));
  const lower = at(cyl(0.03, 0.04, 0.6, mat(P.metal), 6), 0, 0.36, 0);
  g.add(lower, at(cyl(0.008, 0.015, 0.06, mat(P.metal), 4), 0.01, 0.68, 0, 0, 0, 0.3), tipMount(lower, 0.6));
  if (PS) g.add(pAt(d, 0, 0.072, 0.04, 0, pLadder(0.6 * d.u - 1.5)), pAt(d, 0, 0, 0.2, 0, pStairs(0.072 * d.u)));   // 63.7: a ladder up the standing mast, steps onto the pad
  g.add(at(cyl(0.025, 0.03, 0.8, mat(P.metal), 6), -0.24, 0.34, 0.05, 0, 0, 2.5));             // upper mast, snapped
  g.add(at(rustPatch(0.05, 0.1), 0.03, 0.25, 0.02), at(rustPatch(0.06, 0.1), -0.26, 0.26, 0.085, 0, 0, 2.5));
  const dishMat = mat(P.metalLt); dishMat.side = THREE.DoubleSide;
  g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.15, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.35), dishMat), -0.48, 0.12, 0.08, -1.2, 0, 0.5));
  g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.15, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.35), dishMat), -0.25, 0.12, 0.22, -Math.PI / 2 + 0.2, 0, 0.8));
  g.add(at(box(0.15, 0.12, 0.1, mat(P.metalDk)), 0.16, 0.12, -0.12), at(box(0.08, 0.05, 0.006, mat(KIT.glassDark)), 0.16, 0.14, -0.069));
  g.add(at(cyl(0.006, 0.006, 0.3, mat(P.wire), 4), 0.05, 0.01, -0.2, 0, 0.3, Math.PI / 2));
  g.add(guyWire(new THREE.Vector3(0.0, 0.55, 0), new THREE.Vector3(0.32, 0.06, 0.28)));
  g.add(at(bentPost(0.28, P.wire), 0.3, 0.0, -0.3));
  const burn = scorch(0.28); burn.position.set(-0.3, 0.004, 0.1);
  g.add(burn, rubble(DERELICT, rng, 5, 0.4), reclaim(rng, 4, 0.42));
  return g;
}

const BUILDERS: Record<RuinType, (rng: KitRng, d: Detail) => THREE.Group> = {
  antenna, refinery, surveyStation, landingPad, supplyCache, weatherStation, habitat, commsRelay,
};
const ORDER = Object.keys(BUILDERS) as RuinType[];

/**
 * A ruin. `scale` 'classic' is today's model (planet-scaled by the caller); 'person' (63.7, decision 135 B) sizes it
 * by function — BUILDING_PERSON_SCALE.ruin's `u` — with its doors, windows, stairs, ladders and cargo at REFERENCE,
 * in person units (a person is 1.8).
 */
export function buildRuinModel(ruinType: RuinType, scale: 'classic' | 'person' = 'classic'): THREE.Group {
  const rng = new KitRng(11 + ORDER.indexOf(ruinType) * 5);
  if (scale === 'classic') return BUILDERS[ruinType](rng, CLASSIC_DETAIL);
  const u = BUILDING_PERSON_SCALE.ruin[ruinType].u, g = BUILDERS[ruinType](rng, { u });
  g.scale.setScalar(u);
  return group(g);
}

// ---------------------------------------------------------------------------
// CLAIM SIGN — a team's signpost planted beside a ruin it is claiming / has claimed
// ---------------------------------------------------------------------------

export type ClaimSignState = 'pending' | 'active' | 'broken';

/** The visual state a ruin's claim fields imply, or null when unclaimed. */
export function claimSignState(ruin: RuinState): ClaimSignState | null {
  if (ruin.claimTeamIndex === undefined || ruin.claimTeamIndex < 0) return null;
  if (ruin.claimBroken) return 'broken';
  return ruin.claimBenefitActive ? 'active' : 'pending';
}

/** Key that changes whenever the sign must be rebuilt. */
export function claimSignKey(ruin: RuinState): string | null {
  const s = claimSignState(ruin);
  return s ? `${ruin.claimTeamIndex}:${s}` : null;
}

/**
 * Team signpost for a claimed ruin, in ruin-local units (it rides the ruin's prop
 * scale). Pending claims are a half-raised post; active ones stand full height with
 * a lamp lit; broken ones are knocked over.
 */
export function buildClaimSign(teamIndex: number, state: ClaimSignState): THREE.Group {
  const team = TEAMS[teamIndex % TEAMS.length];
  const g = new THREE.Group();
  const post = signPost(team, state === 'broken', 0.34);
  if (state === 'pending') post.scale.y = 0.6;
  g.add(post);
  if (state === 'active') g.add(at(sphere(0.022, matE(team.visor), 8, 6), 0, 0.42, 0));   // lit: the claim pays out
  g.position.set(0.38, 0, 0.38);
  return g;
}
